import {
  FormInput,
  FormLocation,
  Loader,
  PageActionsStyles,
  PageMainContent,
  Typography,
} from '@/components'
import { FormProvider, useSnackbar } from '@/providers'
import { paths } from '@/routes/paths'
import {
  useCompany,
  useRequestEditCompany,
  useUpdateAddress,
  useUpdateCompany,
} from '@/services'
import { Company } from '@/types/company'
import { useGoBack, useGoToPath, useHistoryState } from '@/utils'
import { Button } from '@surein/ui'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { makeInitialValues } from './helpers'
import { validationSchema } from './validationSchema'

interface FormValues {
  name: string
  address: {
    addressId?: number
    city: string
    country: string
    postalCode: string
    street: string
    streetNr: string
  }
}

const PageForm = ({ data }: { data: Company }) => {
  const { t } = useTranslation()
  const { from } = useHistoryState()
  const snackbar = useSnackbar()
  const { companyId = '' } = useParams()
  const updateCompanyMutation = useUpdateCompany(companyId)
  const updateAddressMutation = useUpdateAddress(data.address.addressId)
  const requestEditCompanyMutation = useRequestEditCompany(companyId)
  const goAfterSubmit = useGoToPath(from || paths.dashboard)

  const handleBack = useGoBack()

  const disabled = data.poaSigned

  useEffect(() => {
    if (disabled) {
      snackbar.info(t('snackbars.success.editRequestDisabled'))
    }
  }, [companyId])

  const editService = (values: FormValues) =>
    disabled
      ? requestEditCompanyMutation.mutateAsync()
      : Promise.all([
          updateCompanyMutation.mutateAsync({
            name: values.name,
          }),
          updateAddressMutation.mutateAsync(values.address),
        ])

  const handleSubmit = (values: FormValues) =>
    editService(values)
      .then(() => {
        snackbar.success(
          disabled
            ? t('snackbars.success.editRequest')
            : t('snackbars.success.editCompany'),
        )
        goAfterSubmit()
      })
      .catch(() => snackbar.error(t('snackbars.error.general')))

  return (
    <FormProvider
      initialValues={makeInitialValues(data)}
      onSubmit={handleSubmit}
      validationSchema={validationSchema(
        t('validations', { returnObjects: true }),
      )}
    >
      <FormInput
        disabled={disabled}
        label={t('companyPage.addCompanyFields.companyName.placeholder')}
        name="name"
      />
      <FormLocation disabled={disabled} name="address" />
      <PageActionsStyles>
        <Button onClick={handleBack} variant="muted" shape="square">
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" shape="square">
          {disabled ? t('common.requestEdit') : t('common.save')}
        </Button>
      </PageActionsStyles>
    </FormProvider>
  )
}

export const EditCompanyPage = () => {
  const { t } = useTranslation()
  const { companyId = '' } = useParams()
  const { data, isLoading } = useCompany(companyId)

  if (isLoading) return <Loader />

  if (!data) return null

  return (
    <PageMainContent>
      <Typography variant="h2">{t('companyPage.editCompany')}</Typography>
      <PageForm data={data} />
    </PageMainContent>
  )
}
